import { CirclePlusIcon, TriangleAlert } from 'lucide-react';
import { useSuspenseQuery } from '@apollo/client/react';

import { withSuspense } from 'src/hoc';
import { BeneficiariesDocument } from 'src/graphql/actions/beneficiary/beneficiaries.query';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BeneficiariesOrderByEnum, QueryOrderEnum } from 'src/graphql/schema';

const ADD_NEW_BENEFICIARY = 'ADD_NEW_BENEFICIARY';
const SELECT_ID = 'invitation-organization-select';
const DESCRIPTION_ID = 'invitation-organization-select-description';
const ERROR_ID = 'invitation-organization-select-error';

type Props = {
  onPick: (companyName: string) => void;
  onAddNew: () => void;
  error?: string;
};

const InvitationOrganizationPicker = ({ onPick, onAddNew, error }: Props) => {
  const { data } = useSuspenseQuery(BeneficiariesDocument, {
    variables: { perPage: 100, order: QueryOrderEnum.Asc, orderBy: BeneficiariesOrderByEnum.CompanyName },
  });

  const organizations = data.beneficiaries.nodes;
  const pickOption = (value: string) => {
    if (value === ADD_NEW_BENEFICIARY) {
      onAddNew();
      return;
    }

    const organization = organizations.find(({ id }) => id === value);

    if (organization) onPick(organization.companyName ?? '');
  };

  return (
    <div className="w-full">
      <Label htmlFor={SELECT_ID}>Choose a beneficiary</Label>
      <Select onValueChange={pickOption}>
        <SelectTrigger
          id={SELECT_ID}
          aria-describedby={error ? `${ERROR_ID} ${DESCRIPTION_ID}` : DESCRIPTION_ID}
          aria-invalid={!!error}
          className="w-full"
        >
          <SelectValue placeholder="Select a beneficiary" />
        </SelectTrigger>
        <SelectContent>
          {organizations.map(({ id, companyName }) => (
            <SelectItem key={id} value={id}>
              {companyName}
            </SelectItem>
          ))}
          {organizations.length > 0 && <SelectSeparator />}
          <SelectItem value={ADD_NEW_BENEFICIARY} className="text-sky-700 font-semibold">
            <CirclePlusIcon className="size-3.5 text-sky-700" /> Add new beneficiary
          </SelectItem>
        </SelectContent>
      </Select>
      {error && (
        <p id={ERROR_ID} className="mt-2 flex items-start gap-1.25 text-xs text-destructive">
          <TriangleAlert className="size-3.5 shrink-0" />
          {error}
        </p>
      )}
      <p id={DESCRIPTION_ID} className="mt-2 text-sm text-muted-foreground">
        You can choose any beneficiary linked to this application, or any organization linked through an accepted vendor
        request.
      </p>
    </div>
  );
};

const InvitationOrganizationPickerSkeleton = () => <Skeleton className="h-8 w-full rounded-lg" />;

export default withSuspense(InvitationOrganizationPicker, InvitationOrganizationPickerSkeleton);
